/**
 * Live Ops Tab
 *
 * Real-time order metrics and fulfillment performance for a store.
 * Shows today's stats and recent orders.
 *
 * @module StoreCommandCenter/components/tabs
 */

import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  LocalShipping as TransitIcon,
  Inventory2 as PackingIcon,
  HourglassEmpty as WaitingIcon,
  Speed as SpeedIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingIcon,
  CheckCircle as CompletedIcon,
} from '@mui/icons-material';
import type { StoreCommandData } from '../../hooks/useCommandCenter';
import { formatMinutes } from '../../hooks/useCommandCenter';
import { useAdminTheme, type AdminColorTokens } from '../../../../admin-theme';

// ============================================================================
// Types
// ============================================================================

interface LiveOpsTabProps {
  store: StoreCommandData;
}

// ============================================================================
// Sub-components
// ============================================================================

interface MetricCardProps {
  icon: React.ReactElement;
  label: string;
  value: string | number;
  subtitle?: string;
  color: string;
  progress?: number;
  colors: AdminColorTokens;
}

/**
 * Large metric card for key stats
 */
function MetricCard({ icon, label, value, subtitle, color, progress, colors }: MetricCardProps) {
  return (
    <Paper
      sx={{
        p: 3,
        bgcolor: colors.bgElevated,
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background glow effect */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 100,
          height: 100,
          borderRadius: '50%',
          bgcolor: `${color}08`,
          filter: 'blur(20px)',
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, position: 'relative' }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${color}15`,
            border: `1px solid ${color}30`,
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 24, color } } as any)}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>
            {label}
          </Typography>
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{ color, letterSpacing: '-0.02em' }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {progress !== undefined && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(progress, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: `${color}15`,
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                bgcolor: color,
              },
            }}
          />
        </Box>
      )}
    </Paper>
  );
}

/**
 * Order pipeline visualization
 */
function OrderPipeline({ signals, colors }: { signals: StoreCommandData['signals']; colors: AdminColorTokens }) {
  const total = signals.packing + signals.awaitingPickup + signals.inTransit;
  const stages = [
    { label: 'Packing', count: signals.packing, color: colors.warning, icon: <PackingIcon /> },
    { label: 'Awaiting', count: signals.awaitingPickup, color: colors.successGlow, icon: <WaitingIcon /> },
    { label: 'In Transit', count: signals.inTransit, color: colors.info, icon: <TransitIcon /> },
  ];

  return (
    <Paper
      sx={{
        p: 3,
        bgcolor: colors.bgElevated,
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: colors.textMuted, display: 'block', mb: 2, letterSpacing: '0.1em' }}
      >
        Order Pipeline
      </Typography>

      {/* Pipeline visualization */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        {stages.map((stage, index) => (
          <React.Fragment key={stage.label}>
            <Tooltip title={`${stage.label}: ${stage.count} orders`}>
              <Box
                sx={{
                  flex: total > 0 ? stage.count / total : 1,
                  minWidth: 40,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: stage.color,
                  opacity: stage.count > 0 ? 1 : 0.3,
                  transition: 'all 0.3s',
                }}
              />
            </Tooltip>
            {index < stages.length - 1 && (
              <Box
                sx={{
                  color: colors.textMuted,
                  fontSize: 12,
                }}
              >
                →
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>

      {/* Stage cards */}
      <Grid container spacing={2}>
        {stages.map((stage) => (
          <Grid size={4} key={stage.label}>
            <Box
              sx={{
                textAlign: 'center',
                p: 1.5,
                borderRadius: 2,
                bgcolor: `${stage.color}08`,
                border: `1px solid ${stage.color}20`,
              }}
            >
              {React.cloneElement(stage.icon, { sx: { fontSize: 20, color: stage.color, mb: 0.5 } } as any)}
              <Typography variant="h5" fontWeight={700} sx={{ color: stage.color }}>
                {stage.count}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                {stage.label}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {total === 0 && (
        <Typography
          variant="body2"
          sx={{ color: colors.textMuted, textAlign: 'center', mt: 2, fontStyle: 'italic' }}
        >
          No active orders at this time
        </Typography>
      )}
    </Paper>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Live Ops Tab Content
 */
export function LiveOpsTab({ store }: LiveOpsTabProps) {
  const { colors } = useAdminTheme();
  const totalActive = store.signals.packing + store.signals.awaitingPickup + store.signals.inTransit;

  return (
    <Box>
      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <MetricCard
            icon={<SpeedIcon />}
            label="Avg Fulfillment Time"
            value={formatMinutes(store.performance.avgFulfillmentMinutes)}
            subtitle="PLACED → DISPATCHED (24h)"
            color={colors.info}
            colors={colors}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <MetricCard
            icon={<CompletedIcon />}
            label="Orders Completed"
            value={store.performance.ordersCompleted}
            subtitle="Last 24 hours"
            color={colors.successGlow}
            colors={colors}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <MetricCard
            icon={<TrendingIcon />}
            label="Active Orders"
            value={totalActive}
            subtitle="Currently in progress"
            color={totalActive > 10 ? colors.warning : colors.info}
            progress={Math.min((totalActive / 20) * 100, 100)}
            colors={colors}
          />
        </Grid>
      </Grid>

      {/* Order Pipeline */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <OrderPipeline signals={store.signals} colors={colors} />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          {/* Working Hours Card */}
          <Paper
            sx={{
              p: 3,
              bgcolor: colors.bgElevated,
              borderRadius: 2,
              border: `1px solid ${colors.border}`,
              height: '100%',
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: colors.textMuted, display: 'block', mb: 2, letterSpacing: '0.1em' }}
            >
              Today's Schedule
            </Typography>

            {store.workingHours ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TimeIcon sx={{ fontSize: 20, color: colors.info }} />
                  <Typography variant="h5" fontWeight={600} sx={{ color: colors.textPrimary }}>
                    {store.workingHours.open} - {store.workingHours.close}
                  </Typography>
                </Box>
                <Chip
                  label={store.isOpen ? 'Currently Open' : 'Currently Closed'}
                  sx={{
                    bgcolor: store.isOpen ? `${colors.successGlow}15` : `${colors.errorGlow}15`,
                    color: store.isOpen ? colors.successGlow : colors.errorGlow,
                    fontWeight: 600,
                    border: `1px solid ${store.isOpen ? colors.successGlow : colors.errorGlow}30`,
                  }}
                />
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: colors.textMuted, fontStyle: 'italic' }}>
                No working hours set for today
              </Typography>
            )}

            {/* Quick Stats */}
            <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${colors.border}` }}>
              <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 1 }}>
                Inventory Quick View
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" fontWeight={600} sx={{ color: colors.textPrimary }}>
                    {store.inventory.productCount.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    Products
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ color: store.inventory.lowStockCount > 0 ? colors.warning : colors.textPrimary }}
                  >
                    {store.inventory.lowStockCount}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    Low Stock
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default LiveOpsTab;
